import React from 'react'
import { AsyncContentProgress, Badge, Button, Input, Label, Switch } from '@/shared/ui'
import LoadingCandles from '@/shared/ui/LoadingCandles'
import { OpportunityCompactCard } from '@/modules/oportunidades/components/OpportunityCompactCard'
import { useThermometerGate } from '@/modules/termometro/hooks/useThermometerGate'
import { useConfirmedOpportunitiesForLink } from '../hooks/useConfirmedOpportunitiesForLink'
import { useLinkedOpportunityAnalysisCounts } from '../hooks/useLinkedOpportunityAnalysisCounts'

type AnalysisOpportunityLinkFieldProps = {
  enabled: boolean
  selectedOpportunityId: number | null
  onEnabledChange: (enabled: boolean) => void
  onSelectedOpportunityIdChange: (id: number | null) => void
  disabled?: boolean
  showExistingAnalysisBadge?: boolean
  excludeAnalysisId?: number | null
}

export function AnalysisOpportunityLinkField({
  enabled,
  selectedOpportunityId,
  onEnabledChange,
  onSelectedOpportunityIdChange,
  disabled,
  showExistingAnalysisBadge = false,
  excludeAnalysisId = null,
}: AnalysisOpportunityLinkFieldProps) {
  const { items, selected, loading, error } = useConfirmedOpportunitiesForLink(enabled, selectedOpportunityId)
  const { thermoAllowed, noteForMaster } = useThermometerGate()
  const [query, setQuery] = React.useState('')
  const [pickerOpen, setPickerOpen] = React.useState(() => enabled && selectedOpportunityId == null)
  const analysisBadgeEnabled = enabled && showExistingAnalysisBadge
  const opportunityIds = React.useMemo(
    () => (items || []).map((item) => Number(item.id)),
    [items],
  )
  const { map: linkedAnalysisMap } = useLinkedOpportunityAnalysisCounts(
    opportunityIds,
    excludeAnalysisId,
    analysisBadgeEnabled,
  )

  React.useEffect(() => {
    if (!enabled) {
      setPickerOpen(false)
      setQuery('')
      return
    }
    if (selectedOpportunityId == null) {
      setPickerOpen(true)
    }
  }, [enabled, selectedOpportunityId])

  const filtered = React.useMemo(() => {
    const q = String(query || '').trim().toLowerCase()
    if (!q) return items
    return (items || []).filter((it) => {
      const sym = String(it.symbol || '').toLowerCase()
      const name = String(it.name || '').toLowerCase()
      return sym.includes(q) || name.includes(q)
    })
  }, [items, query])

  const showPicker = enabled && (pickerOpen || selectedOpportunityId == null)
  const showSelected = enabled && selectedOpportunityId != null && !showPicker

  function handleSelectOpportunity(id: number) {
    onSelectedOpportunityIdChange(id)
    setPickerOpen(false)
  }

  function handleClearSelection() {
    onSelectedOpportunityIdChange(null)
    setQuery('')
    setPickerOpen(true)
  }

  function renderExistingAnalysisBadge(opportunityId: number) {
    if (!analysisBadgeEnabled) return null
    const count = linkedAnalysisMap[Number(opportunityId)] || 0
    if (count <= 0) return null
    return (
      <Badge variant="soft" color="warning" className="whitespace-nowrap text-[11px]">
        já possui análise
      </Badge>
    )
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0 space-y-0.5">
          <Label>Vincular oportunidade confirmada</Label>
          <p className="text-xs text-foreground/60">
            Opcional. Exibe o card da oportunidade no feed e unifica conversa + reações.
          </p>
        </div>
        <Switch
          checked={enabled}
          disabled={disabled}
          onChange={(e) => {
            const next = (e.target as HTMLInputElement).checked
            onEnabledChange(next)
            if (!next) onSelectedOpportunityIdChange(null)
          }}
        />
      </div>

      {enabled && (
        <div className="relative">
          <div
            className={[
              'transition-all duration-300 ease-out',
              showPicker
                ? 'max-h-[620px] opacity-100 translate-y-0'
                : 'max-h-0 opacity-0 -translate-y-2 overflow-hidden pointer-events-none',
            ].join(' ')}
          >
            <div className="rounded-lg border border-border/40 bg-background/20 p-3">
              <div className="flex items-center gap-2">
                <Input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Buscar oportunidade (ex.: BTC, ETH, SOL...)"
                  disabled={disabled}
                />
              </div>

              {error ? <div className="mt-2 text-xs text-red-400">{error}</div> : null}

              {loading ? (
                <div className="mt-3 flex w-full items-center justify-center py-2">
                  <LoadingCandles className="min-h-[60px]" label="Carregando oportunidades confirmadas..." />
                </div>
              ) : filtered.length === 0 ? (
                <div className="mt-3 text-sm text-foreground/70">
                  Nenhuma oportunidade confirmada encontrada.
                </div>
              ) : (
                <div className="mt-3 max-h-[420px] space-y-3 overflow-y-auto pr-1">
                  {filtered.map((it) => (
                    <OpportunityCompactCard
                      key={it.id}
                      item={it}
                      thermoAllowed={thermoAllowed}
                      noteForMaster={noteForMaster}
                      highlight={selectedOpportunityId != null && Number(it.id) === Number(selectedOpportunityId)}
                      onClick={() => handleSelectOpportunity(Math.floor(Number(it.id)))}
                      interactive={!disabled}
                      showClosedBadge={false}
                      leadingBadge={renderExistingAnalysisBadge(Number(it.id))}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>

          <div
            className={[
              'transition-all duration-300 ease-out',
              showSelected
                ? 'max-h-[260px] opacity-100 translate-y-0'
                : 'max-h-0 opacity-0 translate-y-2 overflow-hidden pointer-events-none',
            ].join(' ')}
          >
            <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
              <div className="min-w-0 flex-1">
                {selected ? (
                  <OpportunityCompactCard
                    item={selected}
                    thermoAllowed={thermoAllowed}
                    noteForMaster={noteForMaster}
                    interactive={false}
                    leadingBadge={renderExistingAnalysisBadge(Number(selected.id))}
                  />
                ) : (
                  <div className="rounded-xl bg-background/20 p-3">
                    {loading ? (
                      <AsyncContentProgress loading label="Carregando oportunidade...">
                        {null}
                      </AsyncContentProgress>
                    ) : (
                      <div className="text-xs text-foreground/70">
                        {`Oportunidade selecionada: #${selectedOpportunityId} (pode estar encerrada).`}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="flex w-full flex-col gap-2 sm:w-auto">
                <Button
                  type="button"
                  variant="outline"
                  className="h-9 w-full sm:w-auto"
                  onClick={() => setPickerOpen(true)}
                  disabled={disabled}
                >
                  Alterar
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="h-9 w-full text-destructive sm:w-auto"
                  onClick={handleClearSelection}
                  disabled={disabled}
                >
                  Excluir
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
